import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { DnaBackground } from "@/components/DnaBackground";
import { Logo } from "@/components/Logo";
import { PasswordInput } from "@/components/PasswordInput";
import { PasswordStrengthIndicator } from "@/components/PasswordStrengthIndicator";
import { TurnstileWidget } from "@/components/TurnstileWidget";
import { LanguageSelector } from "@/components/LanguageSelector";
import { useTranslation } from "react-i18next";
import { authAPI, getAuthToken, getUserData } from "@/lib/api";
import { calculatePasswordStrength, getPasswordRequirements } from "@/lib/password-validation";
import { SECURITY_CONFIG } from "@/config/security";
import { getLocalizedErrorMessage } from "@/lib/error-i18n";

export default function Auth() {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const navigate = useNavigate();

  const getRequiredFieldMessage = (target: HTMLInputElement) => {
    if (target.type === "email" && target.value && target.validity.typeMismatch) {
      return t('auth.invalidEmail');
    }

    return t('auth.fieldRequired');
  };

  const handleCustomValidation = (event: React.InvalidEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;
    event.preventDefault();
    target.setCustomValidity(getRequiredFieldMessage(target));
    target.reportValidity();
  };

  // Password strength calculation
  const passwordStrength = calculatePasswordStrength(password, t);
  const passwordRequirements = getPasswordRequirements(password, t);

  useEffect(() => {
    const authToken = getAuthToken();
    const userData = getUserData();
    const inviteToken = localStorage.getItem("invite_token");

    if (authToken && userData) {
      if (inviteToken) {
        navigate(`/invite/${inviteToken}`);
      } else if (userData.role === "admin") {
        navigate("/admin");
      } else {
        navigate("/dashboard");
      }
    }
  }, [navigate]);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate Turnstile token
    if (SECURITY_CONFIG.turnstile.enableOnAuth && !turnstileToken) {
      toast.error(t('auth.captchaRequired') || 'Please complete the security verification');
      return;
    }
    
    // Validate password confirmation for sign up
    if (isSignUp) {
      if (password !== confirmPassword) {
        toast.error(t('auth.passwordsMismatch') || 'Passwords do not match');
        return;
      }
      
      if (password.length < 6) {
        toast.error(t('auth.passwordTooShort') || 'Password must be at least 6 characters');
        return;
      }
      
      if (!/[A-Z]/.test(password)) {
        toast.error(t('auth.passwordNeedsUppercase') || 'Password must contain at least one uppercase letter');
        return;
      }
      
      if (!/\d/.test(password)) {
        toast.error(t('auth.passwordNeedsNumber') || 'Password must contain at least one number');
        return;
      }
      
      if (!/[^A-Za-z0-9]/.test(password)) {
        toast.error(t('auth.passwordNeedsSpecial') || 'Password must contain at least one special character');
        return;
      }
    }
    
    setLoading(true);

    try {
      if (isSignUp) {
        const response = await authAPI.register({
          name: fullName,
          email,
          password,
          turnstileToken, // Include Turnstile token
        });

        if (response.success) {
          toast.success(t('auth.accountCreated'));
          
          // Redirect to email verification sent page
          navigate('/email-verification-sent', { 
            state: { email } 
          });
        }
      } else {
        const response = await authAPI.login({
          email,
          password,
          turnstileToken, // Include Turnstile token
        });

        if (response.success) {
          toast.success(t('auth.welcomeBackMessage'));
          
          // Check if user needs to change password
          if (response.data?.user.requirePasswordChange) {
            navigate("/change-password");
            return;
          }
          
          // Redirect based on role
          const inviteToken = localStorage.getItem("invite_token");

          if (inviteToken) {
            navigate(`/invite/${inviteToken}`);
            return;
          }

          if (response.data?.user.role === 'admin') {
            navigate("/admin");
          } else {
            navigate("/dashboard");
          }
        }
      }
    } catch (error: any) {
      toast.error(getLocalizedErrorMessage(error, "errors.authentication"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 relative">
      <DnaBackground />

      <div className="absolute right-6 top-6 z-10 flex items-center gap-3">
        <LanguageSelector />
        <Link
          to="/"
          aria-label="Go to home"
          title="Go to home"
          className="rounded-md transition-opacity hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background"
        >
          <Logo size="sm" />
        </Link>
      </div>
      
      <Card className="w-full max-w-md p-8 bg-card border-border shadow-glow animate-scale-in">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gradient mb-2">
            {isSignUp ? t('auth.createAccount') : t('auth.welcomeBack')}
          </h1>
          <p className="text-muted-foreground">
            {isSignUp
              ? t('auth.startJourney')
              : t('auth.signInToContinue')}
          </p>
        </div>

        <form onSubmit={handleAuth} className="space-y-4">
          {isSignUp && (
            <div className="space-y-2 text-start">
              <Label htmlFor="fullName" className="text-foreground font-semibold">
                {t('auth.fullName')}
              </Label>
              <div className="relative">
                <Input
                  id="fullName"
                  type="text"
                  placeholder={t('auth.fullName')}
                  value={fullName}
                  onChange={(e) => {
                    setFullName(e.target.value);
                    e.currentTarget.setCustomValidity("");
                  }}
                  required
                  className="bg-background/50"
                  onInvalid={handleCustomValidation}
                  onInput={(e) => e.currentTarget.setCustomValidity("")}
                />
              </div>
            </div>
          )}
          <div className="space-y-2 text-start">
            <Label htmlFor="email" className="text-foreground font-semibold">
              {t('auth.email')}
            </Label>
            <div className="relative">
              <Input
                id="email"
                type="email"
                placeholder={t('auth.email')}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  e.currentTarget.setCustomValidity("");
                }}
                required
                className="bg-background/50"
                onInvalid={handleCustomValidation}
                onInput={(e) => e.currentTarget.setCustomValidity("")}
              />
            </div>
          </div>
          
          <PasswordInput
            id="password"
            label={t('auth.password')}
            value={password}
            onChange={(value) => {
              setPassword(value);
            }}
            placeholder="••••••••"
            required
            autoComplete={isSignUp ? "new-password" : "current-password"}
            onInvalid={handleCustomValidation}
            onInput={(e) => e.currentTarget.setCustomValidity("")}
          />
          
          {!isSignUp && (
            <div className="text-right">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-sm text-primary hover:underline"
              >
                {t('auth.forgotPassword') || 'Forgot Password?'}
              </button>
            </div>
          )}
          
          {isSignUp && password && (
            <PasswordStrengthIndicator
              strength={passwordStrength}
              requirements={passwordRequirements}
              strengthLabel={t('auth.passwordStrength') || 'Password Strength'}
            />
          )}
          
          {isSignUp && (
            <PasswordInput
              id="confirmPassword"
              label={t('auth.confirmPassword') || 'Confirm Password'}
              value={confirmPassword}
              onChange={setConfirmPassword}
              placeholder="••••••••"
              required
              autoComplete="new-password"
              onInvalid={handleCustomValidation}
              onInput={(e) => e.currentTarget.setCustomValidity("")}
            />
          )}

          {/* Cloudflare Turnstile CAPTCHA */}
          {SECURITY_CONFIG.turnstile.enableOnAuth && (
            <div className="mt-4">
              <TurnstileWidget
                sitekey={SECURITY_CONFIG.turnstile.siteKey}
                theme="auto"
                size="normal"
                onVerify={setTurnstileToken}
                onError={() => {
                  setTurnstileToken(null);
                  toast.error(t('auth.captchaError') || 'Security verification failed');
                }}
                onExpired={() => {
                  setTurnstileToken(null);
                }}
              />
            </div>
          )}

          <Button
            type="submit"
            className="w-full bg-primary hover:bg-primary/90 shadow-glow"
            disabled={loading || (SECURITY_CONFIG.turnstile.enableOnAuth && !turnstileToken)}
          >
            {loading ? t('auth.loading') : isSignUp ? t('auth.signUp') : t('auth.signIn')}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => setIsSignUp(!isSignUp)}
            className="text-sm text-primary hover:underline"
          >
            {isSignUp
              ? `${t('auth.alreadyHaveAccount')} ${t('auth.signIn')}`
              : `${t('auth.dontHaveAccount')} ${t('auth.signUp')}`}
          </button>
        </div>
      </Card>
    </div>
  );
}
